import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useRef } from 'react';

interface ConfirmOptions {
  title: string;
  message: string;
}

interface AlertOptions {
  title: string;
  message: string;
  type?: 'info' | 'error' | 'warning';
}

interface ModalContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  isConfirmOpen: boolean;
  confirmOptions: ConfirmOptions | null;
  resolveConfirm: (result: boolean) => void;
  alert: (options: AlertOptions) => Promise<void>;
  isAlertOpen: boolean;
  alertOptions: AlertOptions | null;
  dismissAlert: () => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(null);
  // Resolvers live in refs: they are never rendered, and a ref lets confirm()
  // settle the previous caller before installing the next one (WEBD-26).
  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertOptions, setAlertOptions] = useState<AlertOptions | null>(null);
  const alertResolverRef = useRef<(() => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      // A confirm opened over a pending one replaces it: the earlier caller
      // gets `false` (not confirmed) instead of a promise that never settles.
      confirmResolverRef.current?.(false);
      confirmResolverRef.current = resolve;
      setConfirmOptions(options);
      setIsConfirmOpen(true);
    });
  }, []);

  const resolveConfirm = useCallback((result: boolean) => {
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    resolve?.(result);
    setIsConfirmOpen(false);
    setConfirmOptions(null);
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise<void>((resolve) => {
      // Same for alerts: the replaced alert counts as dismissed.
      alertResolverRef.current?.();
      alertResolverRef.current = resolve;
      setAlertOptions(options);
      setIsAlertOpen(true);
    });
  }, []);

  const dismissAlert = useCallback(() => {
    const resolve = alertResolverRef.current;
    alertResolverRef.current = null;
    resolve?.();
    setIsAlertOpen(false);
    setAlertOptions(null);
  }, []);

  const value = useMemo<ModalContextValue>(() => ({
    confirm, isConfirmOpen, confirmOptions, resolveConfirm,
    alert, isAlertOpen, alertOptions, dismissAlert,
  }), [confirm, isConfirmOpen, confirmOptions, resolveConfirm, alert, isAlertOpen, alertOptions, dismissAlert]);

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
}
